import React, { useState, useEffect, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import logo from "../Images/logo.png";
import './BookingModal.css';

const API_BASE_URL =
    process.env.NODE_ENV === "development"
        ? "http://localhost:5000"
        : "https://rapidphysio-backend-ka9x.onrender.com";

function loadRazorpayScript(src) {
    return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
}

const BookingModal = ({ isOpen, onClose }) => {
    const getToday = () => new Date().toISOString().split('T')[0];
    const [form, setForm] = useState({
        name: '', email: '', phone: '', date: getToday(), city: '', customCity: '',
        therapyType: '', customTherapyType: '', therapyMode: ''
    });
    const [selectedTime, setSelectedTime] = useState('');
    const [ticketData, setTicketData] = useState(null);
    const [phoneError, setPhoneError] = useState('');
    const therapyTypes = [
        'Physiotherapy', 'Nutrition', 'Speech and Audiology', 'Psychology',
        'Occupational Therapy', 'Strength and Conditioning', "Women's health", 'Other'
    ];
    const [therapyModes, setTherapyModes] = useState([]);
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(false);
    const ticketRef = useRef();

    const generateTimeSlots = () => {
        const slots = [];
        for (let hour = 8; hour <= 21; hour++) {
            const period = hour >= 12 ? 'PM' : 'AM';
            const displayHour = hour % 12 === 0 ? 12 : hour % 12;
            slots.push(`${displayHour.toString().padStart(2, '0')}:00 ${period}`);
        }
        return slots;
    };

    useEffect(() => {
        // Function to update the date
        const updateDate = () => setForm(prev => ({ ...prev, date: getToday() }));

        // Calculate milliseconds until midnight
        const now = new Date();
        const msUntilMidnight = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate() + 1,
            0, 0, 0
        ) - now;

        // Set timeout to update at midnight
        const midnightTimeout = setTimeout(() => {
            updateDate();

            // After midnight, repeat every 24 hours
            const interval = setInterval(updateDate, 24 * 60 * 60 * 1000);
            // Save interval id to clear on unmount
            midnightTimeout.intervalId = interval;
        }, msUntilMidnight);

        return () => {
            clearTimeout(midnightTimeout);
            if (midnightTimeout.intervalId) clearInterval(midnightTimeout.intervalId);
        };
    }, []);

    useEffect(() => {
        if (isOpen) {
            setTherapyModes(['Home Visit', 'Online Consultation']);
            setCities(['Bhubaneswar', 'Rourkela', 'Berhampur', 'Hyderabad', 'Bangalore', 'Mangalore', 'Delhi', 'Kolkata']);
            loadRazorpayScript("https://checkout.razorpay.com/v1/checkout.js");
        }
    }, [isOpen]);

    useEffect(() => {
        const selectedType = form.therapyType;
        if (['Physiotherapy', 'Speech and Audiology', 'Occupational Therapy', 'Other'].includes(selectedType)) {
            setTherapyModes(['Home Visit', 'Online Consultation']);
        } else if (selectedType) {
            setTherapyModes(['Online Consultation']);
            setForm(prev => ({ ...prev, therapyMode: '' }));
        } else {
            setTherapyModes([]);
            setForm(prev => ({ ...prev, therapyMode: '' }));
        }
    }, [form.therapyType]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'phone') {
            const digits = value.replace(/\D/g, '');
            setForm(prev => ({ ...prev, phone: digits }));
            setPhoneError(digits.length === 10 ? '' : 'Phone number must be 10 digits');
        } else if (name === 'date') {
            const selectedDate = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (selectedDate < today) return; // block past dates

            setForm(prev => ({ ...prev, date: value }));
        } else {
            setForm(prev => ({ ...prev, [name]: value }));
        }
    };


    const handlePayment = async (e) => {
        e.preventDefault();

        const isLoaded = await loadRazorpayScript("https://checkout.razorpay.com/v1/checkout.js");
        if (!isLoaded) {
            return alert("Failed to load Razorpay SDK. Please try again.");
        }

        const { name, email, phone, date, city, customCity, therapyType, customTherapyType, therapyMode } = form;
        const selectedCity = city === "Other" ? customCity : city;
        const selectedTherapy = therapyType === "Other" ? customTherapyType : therapyType;

        if (!name || !email || !phone || !date || !selectedCity || !selectedTherapy || !therapyMode || !selectedTime) {
            return alert("Please fill all the fields");
        }

        const bookingData = {
            ...form,
            time: selectedTime,
        };

        setLoading(true);
        const amount = 99;

        try {
            // 1️⃣ Fetch Razorpay Key
            const keyRes = await fetch(`${API_BASE_URL}/api/payment/razorpay-key`);
            const { key } = await keyRes.json();
            if (!key) throw new Error("Failed to fetch Razorpay key");

            // 2️⃣ Create Order
            const orderRes = await fetch(`${API_BASE_URL}/api/payment/create-order`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ amount, bookingData }),
            });

            const orderData = await orderRes.json();
            if (!orderRes.ok || !orderData.id) throw new Error(orderData.message || "Failed to create order");

            // 3️⃣ Open Razorpay Checkout
            const options = {
                key,
                amount: orderData.amount,
                currency: orderData.currency,
                name: "Rapid Physio",
                description: "Appointment Booking",
                order_id: orderData.id,
                handler: async function (response) {
                    try {
                        // 4️⃣ Verify Payment & Save Booking
                        const verifyRes = await fetch(`${API_BASE_URL}/api/payment/verify-payment`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                                ...response,
                                bookingData,
                            }),
                        });

                        const result = await verifyRes.json();

                        // if (verifyRes.ok && result.booking) {
                        //     // ✅ Use bookingRef from backend
                        //     setTicketData({ ...result.booking });
                        //     alert(`✅ Payment successful! Your Booking Ref: ${result.booking.bookingRef} has been sent to your email.`);
                        // } else {
                        //     alert("Booking failed: " + (result.message || "Unknown error"));
                        // }
                        if (verifyRes.ok && result.booking) {

                            console.log("✅ Booking:", result.booking);

                            // ✅ FIX: Proper order
                            setLoading(false);
                            setTicketData(result.booking);

                            // 🔥 Ensure re-render
                            setTimeout(() => {
                                setTicketData(result.booking);
                            }, 50);

                        } else {
                            alert("Booking failed");
                            setLoading(false);
                        }
                    } catch (err) {
                        console.error(err);
                        setLoading(false);
                    }
                },
                prefill: { name, email, contact: phone },
                method: { upi: true, card: true, netbanking: true },
                theme: { color: "#4CAF50" },
            };

            const rzp = new window.Razorpay(options);

            rzp.on("payment.failed", function (response) {
                alert("❌ Payment failed: " + response.error.description);
            });

            console.log("Current Domain:", window.location.origin);
            console.log("Order ID:", orderData.id);
            console.log("Razorpay Key:", key);
            rzp.open();

        } catch (err) {
            console.error(err);
            alert("Payment initiation failed: " + err.message);
        } finally {
            setLoading(false);
        }
    };



    const handleDownload = async () => {
        const html2canvas = (await import("html2canvas")).default;
        const jsPDF = (await import("jspdf")).default;

        const ticketElement = ticketRef.current;

        // 🔹 Hide action buttons before capturing
        const actions = ticketElement.querySelector(".ticket-actions");
        if (actions) actions.style.display = "none";

        // Capture ticket
        const canvas = await html2canvas(ticketElement, { scale: 2 });
        const imgData = canvas.toDataURL("image/png");

        // Create PDF (A4)
        const pdf = new jsPDF("p", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
        pdf.save(`RapidPhysio_Ticket_${ticketData.bookingRef}.pdf`);

        // 🔹 Restore action buttons after PDF is generated
        if (actions) actions.style.display = "flex";
    };


    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="close-btn" onClick={onClose}>×</button>

                {loading && <p>Processing Payment...</p>}

                {!ticketData && !loading ? (
                    <form className="booking-form" onSubmit={handlePayment}>
                        <h2>Book Appointment</h2>
                        <input name="name" type="text" placeholder="Name" value={form.name} onChange={handleChange} required />
                        <input name="email" type="email" placeholder="Email" value={form.email} onChange={handleChange} required />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ padding: '8px', background: '#eee' }}>+91</span>
                            <input
                                name="phone"
                                type="tel"
                                placeholder="Phone Number"
                                value={form.phone}
                                onChange={handleChange}
                                maxLength={10}
                                style={{ flex: 1, border: phoneError ? '1px solid red' : '1px solid #ccc', padding: '8px' }}
                                required
                            />
                        </div>
                        {phoneError && <p style={{ color: 'red', fontSize: '13px' }}>{phoneError}</p>}

                        <input
                            name="date"
                            type="date"
                            value={form.date}
                            onChange={handleChange}
                            min={getToday()}
                            required
                        />

                        <select name="city" value={form.city} onChange={handleChange} required>
                            <option value="">Select City</option>
                            {cities.map((city, idx) => <option key={idx} value={city}>{city}</option>)}
                            <option value="Other">Other</option>
                        </select>

                        {form.city === 'Other' && (
                            <input
                                name="customCity"
                                type="text"
                                placeholder="Enter your city"
                                value={form.customCity || ''}
                                onChange={handleChange}
                                required
                            />
                        )}

                        <select name="therapyType" value={form.therapyType} onChange={handleChange} required>
                            <option value="">Select Therapy</option>
                            {therapyTypes.map((type, idx) => <option key={idx} value={type}>{type}</option>)}
                        </select>

                        {form.therapyType === 'Other' && (
                            <input
                                name="customTherapyType"
                                type="text"
                                placeholder="Enter your therapy type"
                                value={form.customTherapyType || ''}
                                onChange={handleChange}
                                required
                            />
                        )}

                        {therapyModes.length > 0 && (
                            <select name="therapyMode" value={form.therapyMode} onChange={handleChange} required>
                                <option value="">Select Therapy Mode</option>
                                {therapyModes.map((mode, idx) => (
                                    <option key={idx}>{mode}</option>
                                ))}
                            </select>
                        )}

                        <select value={selectedTime} onChange={(e) => setSelectedTime(e.target.value)} required>
                            <option value="">Select Time Slot</option>
                            {generateTimeSlots().map((slot, idx) => (
                                <option key={idx} value={slot}>{slot}</option>
                            ))}
                        </select>

                        <button type="submit" disabled={form.phone.length !== 10}>
                            Pay ₹99 & Book Appointment
                        </button>
                    </form>
                ) : ticketData && !loading ? (
                    <div className="ticket" ref={ticketRef}>
                        {/* Action Buttons */}
                        <div className="ticket-actions">
                            <div className="button-group">
                                <button className="download-btn" onClick={handleDownload}>
                                    ⬇ Download
                                </button>
                                <button
                                    className="close-ticket-btn"
                                    onClick={() => {
                                        setTicketData(null);
                                        setForm({
                                            name: "",
                                            email: "",
                                            phone: "",
                                            date: "",
                                            city: "",
                                            customCity: "",
                                            therapyType: "",
                                            customTherapyType: "",
                                            therapyMode: "",
                                        });
                                        setSelectedTime("");
                                    }}
                                >
                                    × Close
                                </button>
                            </div>
                        </div>

                        {/* Header with Logo */}
                        <div className="ticket-header">
                            <img src={logo} alt="Logo" className="ticket-logo" />
                            <h3>Rapid Physio - Appointment Ticket</h3>
                        </div>

                        {/* Ticket Details */}
                        <div className="ticket-box">
                            <p><strong>Booking Ref:</strong> {ticketData.bookingRef}</p>
                            <p><strong>Name:</strong> {ticketData.name}</p>
                            <p><strong>Email:</strong> {ticketData.email}</p>
                            <p><strong>Phone:</strong> {ticketData.phone}</p>
                            <p><strong>City:</strong> {ticketData.city}</p>
                            <p><strong>Date:</strong> {ticketData.date}</p>
                            <p><strong>Time:</strong> {ticketData.time}</p>
                            <p><strong>Therapy:</strong> {ticketData.therapyType}</p>
                            <p><strong>Mode:</strong> {ticketData.therapyMode}</p>
                            <p><strong>Payment ID:</strong> {ticketData.paymentId}</p>

                            {/* QR Code */}
                            <div className="qr-container">
                                <QRCodeCanvas value={JSON.stringify(ticketData)} size={100} />
                                <p>Scan to verify</p>
                            </div>
                        </div>

                        {/* Footer */}
                        <p className="ticket-footer">
                            Please bring this ticket (digital or printed) to your appointment. <br />
                            PDF ticket has also been emailed to <b>{ticketData.email}</b>.
                        </p>
                    </div>
                ) : null}


            </div>
        </div>
    );
};

export default BookingModal;
